import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ChartWrapperInstance, ChartWrapperService, executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { enrichPriceAnalysisRows } from './price-analysis-formulas';
import { PriceAnalysisChartConfig, PriceAnalysisDataRow } from './price-analysis.types';

@Component({
  selector: 'app-price-analysis-chart',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './price-analysis-chart.component.html',
  styleUrls: ['./price-analysis-chart.component.scss'],
})
export class PriceAnalysisChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  private readonly maxVisibleGroupLimit = 5;
  private chartHostRef?: ElementRef<HTMLDivElement>;

  @ViewChild('chartHost') set chartHost(value: ElementRef<HTMLDivElement> | undefined) {
    if (value && this.myChart && this.myChart.getDom() !== value.nativeElement) {
      this.myChart.dispose();
      this.myChart = undefined;
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
    }
    this.chartHostRef = value;
    this.observeChartHost();
    if (value && !this.loading && this.filteredRows.length) {
      requestAnimationFrame(() => this.renderChart());
    }
  }

  get chartHost(): ElementRef<HTMLDivElement> | undefined {
    return this.chartHostRef;
  }

  loading = true;
  error = '';
  rows: PriceAnalysisDataRow[] = [];
  filteredRows: PriceAnalysisDataRow[] = [];
  pageStartIndex = 0;

  protected myChart?: echarts.ECharts;
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private globalFilterService = inject(GlobalFilterService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private reflowSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private resizeRafId: number | null = null;
  private pagerRafId: number | null = null;

  get config(): PriceAnalysisChartConfig {
    return this.data?.chartConfig || this.data?.dashboardConfig || this.data?.config || this.data;
  }

  get chartHeight(): string {
    return String(this.data?.height || this.config?.['height' as keyof PriceAnalysisChartConfig] || '48vh').trim();
  }

  get currentTitle(): string {
    return String(
      this.data?.title ||
      this.config?.title ||
      this.data?.chartConfig?.title ||
      '',
    ).trim();
  }

  get bottomActions(): any[] {
    const actions = this.config?.bottomActions || this.config?.footerActions || this.data?.bottomActions || this.data?.footerActions || [];
    return Array.isArray(actions) ? actions.filter((action) => action?.visible !== false) : [];
  }

  get hasBottomActions(): boolean {
    return this.bottomActions.length > 0;
  }

  trackByBottomAction = (index: number, action: any): string =>
    String(action?.key || action?.id || action?.label || index);

  onBottomActionClick(action: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!action || action.disabled === true) return;

    const filters = this.globalFilterService.getCurrentFilters?.() || {};
    const routeQueryParams = { ...(this.route?.snapshot?.queryParams || {}) };
    const customerName = this.resolveCurrentCustomerName(filters, routeQueryParams);
    const equipment = this.resolveCurrentEquipment(filters, routeQueryParams);

    executeNavigationFromConfig(
      this.router,
      {
        ...(this.data || {}),
        navigateAction: action,
      },
      {
        name: action.label || action.title || action.key || '',
        value: action.value,
        data: {
          raw: action,
        },
        componentType: 'chart-bottom-action',
      },
      {
        event: {
          name: action.label || action.title || action.key || '',
          value: action.value,
          raw: action,
          componentType: 'chart-bottom-action',
        },
        data: this.data || {},
        filters,
        route: {
          queryParams: routeQueryParams,
        },
        context: {
          widgetId: this.data?.widgetId || this.data?.chartId,
          filters,
          customer_name: customerName,
          customerName,
          equipment,
        },
        widgetId: this.data?.widgetId || this.data?.chartId,
      },
    );
  }

  private resolveCurrentCustomerName(filters: any, routeQueryParams: Record<string, any>): string {
    return this.firstScalar(
      routeQueryParams?.['customer_name'],
      filters?.requestParams?.['customer_name'],
      filters?.queryParams?.['customer_name'],
      this.config?.graphDataSource?.defaultRequestData?.['customer_name'],
      this.data?.defaultRequestData?.['customer_name'],
    );
  }

  private resolveCurrentEquipment(filters: any, routeQueryParams: Record<string, any>): string {
    const firstRow = this.filteredRows?.[this.pageStartIndex] || this.filteredRows?.[0] || this.rows?.[0];
    return this.firstScalar(
      routeQueryParams?.['equipment'],
      filters?.requestParams?.['equipment'],
      filters?.queryParams?.['equipment'],
      this.config?.graphDataSource?.defaultRequestData?.['equipment'],
      this.data?.defaultRequestData?.['equipment'],
      firstRow?.equipment,
      firstRow?.equipmentCpl,
    );
  }

  private firstScalar(...values: any[]): string {
    for (const value of values) {
      const flattened = Array.isArray(value) ? value.flat(Infinity) : [value];
      const match = flattened.find((item) => item !== null && item !== undefined && String(item).trim() !== '');
      if (match !== undefined) {
        return String(match).trim();
      }
    }
    return '';
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadData(filters),
      getChart: () => this.myChart || null,
      getSeriesNames: () => this.config.series?.map((series) => series.label) || [],
      getCategories: () => this.filteredRows.map((row) => String(row[this.config.xAxis.categoryField] || '')),
    });
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
      this.updateComparisonGraphics();
    });
    this.loadData(initialFilters ?? undefined);
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
    }
    if (this.pagerRafId !== null) {
      cancelAnimationFrame(this.pagerRafId);
    }
    this.wrapper.detach();
    this.myChart?.dispose();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.myChart?.resize();
    this.updateComparisonGraphics();
  }

  loadData(filters?: any): void {
    this.loading = true;
    this.error = '';
    const inlineRows = this.config?.data;

    if (Array.isArray(inlineRows) && inlineRows.length) {
      this.setRows(inlineRows);
      return;
    }

    const graphConfig = this.config.graphDataSource;
    const apiUrl = graphConfig?.apiUrl || this.data?.apiUrl;
    const requestData = graphConfig?.apiUrl
      ? {
          method: graphConfig.method || 'post',
          functionName: graphConfig.functionName || this.data?.functionName,
          version: graphConfig.version || this.data?.version || '1.0',
          defaultRequestData: graphConfig.defaultRequestData || this.data?.defaultRequestData,
          headers: graphConfig.headers || this.data?.headers,
          skipDefaultPayload: graphConfig.skipDefaultPayload !== false,
          customPayload: this.buildGraphPayload(filters?.requestParams, graphConfig.customPayload, graphConfig.payloadWrapper),
        }
      : this.data;

    this.configService.getConfig(apiUrl, requestData)?.subscribe({
      next: (res: any) => {
        const responseAtPath = this.extractByPath(res, graphConfig?.responsePath);
        this.setRows(this.extractRows(responseAtPath ?? res));
      },
      error: () => {
        this.error = 'Unable to load price analysis data.';
        this.loading = false;
      },
    });
  }

  drilldown(): void {
    const drilldown = this.config.drilldown;
    if (!drilldown?.enabled) return;
    const firstRow = this.filteredRows[0];
    const payload = Object.fromEntries((drilldown.emitFields || []).map((field) => [field, firstRow?.[field]]));
    console.info('Price analysis drilldown', drilldown.targetLevel, payload);
  }

  private setRows(rows: PriceAnalysisDataRow[]): void {
    this.rows = this.sortRows(enrichPriceAnalysisRows(rows.map((row) => this.normalizeGraphRow(row))));
    this.filteredRows = [...this.rows];
    this.pageStartIndex = 0;
    this.loading = false;
    requestAnimationFrame(() => this.renderChart());
  }

  private sortRows(rows: PriceAnalysisDataRow[]): PriceAnalysisDataRow[] {
    const sortConfig = (this.config as any).sort || {};
    const field = String(sortConfig.field || '').trim();
    if (!field) {
      return rows;
    }

    const direction = String(sortConfig.direction || 'asc').toLowerCase() === 'desc' ? -1 : 1;
    const type = String(sortConfig.type || 'natural').toLowerCase();
    const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });

    return [...rows].sort((left, right) => {
      const leftValue = left?.[field];
      const rightValue = right?.[field];
      if (type === 'number') {
        return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * direction;
      }

      return collator.compare(String(leftValue ?? ''), String(rightValue ?? '')) * direction;
    });
  }

  private extractRows(response: any): PriceAnalysisDataRow[] {
    const dataKey = this.data?.dataKey || 'data';
    const source =
      (Array.isArray(response) && response) ||
      (Array.isArray(response?.value) && response.value) ||
      (Array.isArray(response?.data) && response.data) ||
      response?.data?.[dataKey] ||
      response?.[dataKey];
    if (Array.isArray(source)) return source;
    return response && typeof response === 'object' ? [response] : [];
  }

  private normalizeGraphRow(row: any): PriceAnalysisDataRow {
    const fieldMap = this.config.graphDataSource?.fieldMap || {};
    const get = (targetKey: string, fallbackKey: string, fallbackValue: unknown = '') =>
      row?.[fieldMap[targetKey] || fallbackKey] ?? row?.[targetKey] ?? fallbackValue;

    return {
      ...row,
      year: get('year', 'year'),
      region: String(get('region', 'region')),
      country: String(get('country', 'country')),
      customerId: String(get('customerId', 'customerId', get('customerName', 'customerName'))),
      customerName: String(get('customerName', 'customerName')),
      cpl: String(get('cpl', 'cpl')),
      equipment: String(get('equipment', 'equipment')),
      engine: String(get('engine', 'engine')),
      listPrice: Number(get('listPrice', 'listPrice', 0)) || 0,
      customerTransactedPrice: get('customerTransactedPrice', 'customerTransactedPrice', null) == null
        ? null
        : Number(get('customerTransactedPrice', 'customerTransactedPrice', 0)) || 0,
      customerDiscountPercent: Number(get('customerDiscountPercent', 'customerDiscountPercent', NaN)),
    };
  }

  private buildGraphPayload(
    requestParams: Record<string, unknown> = {},
    basePayload: Record<string, unknown> = {},
    payloadWrapper?: 'doubleArray' | 'array' | 'value',
  ): Record<string, unknown> {
    return Object.entries(requestParams || {}).reduce((payload, [key, value]) => {
      const values = Array.isArray(value) ? value.flat(Infinity) : [value];
      const selectedValues = values.filter((item) => item !== null && item !== undefined && item !== '');
      if (selectedValues.length) {
        if (payloadWrapper === 'doubleArray') {
          payload[key] = [selectedValues];
        } else if (payloadWrapper === 'value') {
          payload[key] = selectedValues.length === 1 ? selectedValues[0] : selectedValues;
        } else {
          payload[key] = selectedValues;
        }
      }
      return payload;
    }, { ...basePayload });
  }

  private extractByPath(source: any, path?: string): any {
    if (!path) return source;
    return path.split('.').reduce((current, key) => current?.[key], source);
  }

  private renderChart(): void {
    if (!this.chartHost?.nativeElement || this.loading) return;
    this.observeChartHost();
    if (!this.myChart) {
      this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
    }

    const categories = this.filteredRows.map((row) => String(row[this.config.xAxis.categoryField] || ''));
    const visibleRows = this.visibleRows;
    const visibleCategories = visibleRows.map((row) => String(row[this.config.xAxis.categoryField] || ''));
    const secondaryXAxisConfig = this.config.secondaryXAxis || (this.config as any).secondXAxis || (this.config as any).secondaryAxis;
    const showSecondaryXAxis = secondaryXAxisConfig?.show === true;
    const secondaryCategories = showSecondaryXAxis
      ? this.resolveSecondaryXAxisLabels(visibleRows, secondaryXAxisConfig)
      : [];
    const series = (this.config.series || []).map((seriesConfig) => ({
      name: seriesConfig.label,
      type: 'bar',
      xAxisIndex: 0,
      yAxisIndex: 0,
      barWidth: (seriesConfig as any).barWidth ?? (this.config as any).barWidth ?? 16,
      barMaxWidth: (seriesConfig as any).barMaxWidth ?? (this.config as any).barMaxWidth ?? 24,
      barGap: (this.config as any).barGap ?? '30%',
      itemStyle: {
        color: seriesConfig.color,
        borderRadius: (this.config as any).barBorderRadius ?? [2, 2, 0, 0],
      },
      emphasis: { focus: 'series' },
      label: {
        show: this.config.layout?.showValueLabels === true && (this.config.layout as any)?.showBarValueLabels !== false,
        position: (this.config as any).barLabelPosition || 'top',
        distance: Number((this.config as any).dataLabelDistance ?? 4),
        color: (this.config as any).dataLabelColor || '#111827',
        fontSize: Number((this.config as any).dataLabelFontSize ?? 8),
        fontWeight: (this.config as any).dataLabelFontWeight ?? 700,
        width: Number((this.config as any).dataLabelWidth ?? 42),
        align: 'center',
        overflow: (this.config as any).dataLabelOverflow || 'truncate',
        formatter: (params: any) => this.formatCompactNumber(params.value),
      },
      data: visibleRows.map((row) => {
        const rawValue = row[seriesConfig.key];
        return {
          value: rawValue == null ? 0 : Number(rawValue),
          row,
        };
      }),
    }));
    this.myChart.setOption({
      color: (this.config.series || []).map((seriesConfig) => seriesConfig.color),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => this.buildTooltip(params?.[0]?.data?.row),
      },
      legend: {
        show: this.config.layout?.showLegend !== false,
        type: (this.config as any).legendType ?? 'plain',
        orient: 'horizontal',
        top: Number((this.config as any).legendTop ?? 6),
        left: (this.config as any).legendLeft ?? 0,
        itemWidth: Number((this.config as any).legendItemWidth ?? 12),
        itemHeight: Number((this.config as any).legendItemHeight ?? 12),
        itemGap: Number((this.config as any).legendItemGap ?? 18),
        selectedMode: false,
        data: (this.config.series || []).map((seriesConfig) => seriesConfig.label),
        textStyle: {
          color: (this.config as any).legendTextColor || '#374151',
          fontSize: Number((this.config as any).legendFontSize ?? 11),
          fontWeight: (this.config as any).legendFontWeight ?? 500,
        },
      },
      grid: {
        left: Number((this.config as any).gridLeft ?? 56),
        right: Number((this.config as any).gridRight ?? 24),
        top: Number((this.config as any).gridTop ?? (this.config.layout?.showDiscountCallouts ? 86 : 54)),
        bottom: Number((this.config as any).gridBottom ?? (showSecondaryXAxis ? 54 : this.hasPagedScroller ? 24 : 44)),
        containLabel: true,
      },
      xAxis: [
        {
          type: 'category',
          data: visibleCategories,
          name: this.config.xAxis.label,
          nameLocation: 'middle',
          nameGap: showSecondaryXAxis
            ? Number((this.config as any).xAxisNameGapWithSecondary ?? 42)
            : Number((this.config as any).xAxisNameGap ?? 34),
          axisLabel: {
            interval: 0,
            rotate: Number((this.config as any).xAxisLabelRotate ?? (categories.length > 4 ? 18 : 0)),
            width: Number((this.config as any).xAxisLabelWidth ?? 90),
            overflow: (this.config as any).xAxisLabelOverflow || 'truncate',
            color: (this.config as any).xAxisLabelColor || '#6b7280',
            fontSize: Number((this.config as any).xAxisFontSize ?? 11),
            fontWeight: (this.config as any).xAxisFontWeight ?? 500,
            margin: Number((this.config as any).xAxisLabelMargin ?? 10),
          },
          axisTick: { show: false },
          axisLine: { lineStyle: { color: (this.config as any).axisLineColor || '#d1d5db' } },
        },
        ...(showSecondaryXAxis
          ? [{
              type: 'category',
              data: secondaryCategories,
              position: 'bottom',
              offset: Number(secondaryXAxisConfig.offset ?? 20),
              name: secondaryXAxisConfig.label || '',
              nameLocation: 'middle',
              nameGap: Number(secondaryXAxisConfig.nameGap ?? 18),
              axisLabel: {
                interval: 0,
                rotate: Number(secondaryXAxisConfig.rotate ?? 0),
                width: Number(secondaryXAxisConfig.width ?? 120),
                overflow: secondaryXAxisConfig.overflow || 'truncate',
                color: secondaryXAxisConfig.labelColor || '#64748b',
                fontSize: Number(secondaryXAxisConfig.fontSize ?? 10),
                fontWeight: secondaryXAxisConfig.fontWeight ?? 500,
                margin: Number(secondaryXAxisConfig.labelMargin ?? 4),
              },
              axisTick: { show: secondaryXAxisConfig.showTicks === true, alignWithLabel: true },
              axisLine: {
                show: secondaryXAxisConfig.showAxisLine === true,
                lineStyle: { color: secondaryXAxisConfig.axisLineColor || (this.config as any).axisLineColor || '#d1d5db' },
              },
            }]
          : []),
      ],
      yAxis: {
        type: 'value',
        name: this.config.yAxis.label,
        nameLocation: 'middle',
        nameGap: Number((this.config as any).yAxisNameGap ?? 54),
        splitLine: {
          show: (this.config as any).showSplitLine !== false,
          lineStyle: {
            color: (this.config as any).splitLineColor || '#e5e7eb',
            type: (this.config as any).splitLineType || 'solid',
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: (this.config as any).yAxisLabelColor || '#9ca3af',
          fontSize: Number((this.config as any).yAxisFontSize ?? 11),
          formatter: (value: number) => this.formatCompactCurrency(value),
        },
        nameTextStyle: {
          color: (this.config as any).yAxisNameColor || '#6b7280',
          fontSize: Number((this.config as any).yAxisNameFontSize ?? 12),
          fontWeight: (this.config as any).yAxisNameFontWeight ?? 700,
        },
      },
      series,
    }, true);

    this.updateComparisonGraphics();
    this.wrapper.afterRender();
  }

  private observeChartHost(): void {
    const target = this.chartHostRef?.nativeElement;
    if (!target || typeof ResizeObserver === 'undefined') return;

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeRafId !== null) {
        cancelAnimationFrame(this.resizeRafId);
      }
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.myChart?.resize();
        this.updateComparisonGraphics();
      });
    });
    this.resizeObserver.observe(target);
  }

  get maxVisibleGroups(): number {
    const configuredLimit = Number((this.config.layout as any)?.maxVisibleGroups || this.maxVisibleGroupLimit);
    return Math.max(1, Math.min(configuredLimit, this.maxVisibleGroupLimit));
  }

  get hasPagedScroller(): boolean {
    return this.filteredRows.length > this.maxVisibleGroups;
  }

  get visibleRows(): PriceAnalysisDataRow[] {
    return this.filteredRows.slice(this.pageStartIndex, this.pageStartIndex + this.maxVisibleGroups);
  }

  get pageEndIndex(): number {
    return Math.min(this.pageStartIndex + this.maxVisibleGroups, this.filteredRows.length);
  }

  get canPageBackward(): boolean {
    return this.pageStartIndex > 0;
  }

  get canPageForward(): boolean {
    return this.pageEndIndex < this.filteredRows.length;
  }

  get pagerMax(): number {
    return Math.max(0, this.filteredRows.length - this.maxVisibleGroups);
  }

  get currentPage(): number {
    return this.pageStartIndex;
  }

  set currentPage(page: number) {
    this.pageStartIndex = Math.max(0, Math.min(Number(page || 0), this.pagerMax));
    this.renderChart();
  }

  onPagerInput(event: Event): void {
    const value = Number((event.target as HTMLInputElement)?.value || 0);
    this.pageStartIndex = Math.max(0, Math.min(value, this.pagerMax));

    if (this.pagerRafId !== null) {
      cancelAnimationFrame(this.pagerRafId);
    }

    this.pagerRafId = requestAnimationFrame(() => {
      this.pagerRafId = null;
      this.renderChart();
    });
  }

  pageBackward(): void {
    this.pageStartIndex = Math.max(0, this.pageStartIndex - this.maxVisibleGroups);
    this.renderChart();
  }

  pageForward(): void {
    const nextStart = this.pageStartIndex + this.maxVisibleGroups;
    this.pageStartIndex = nextStart >= this.filteredRows.length ? this.pageStartIndex : nextStart;
    this.renderChart();
  }

  private buildTooltip(row?: PriceAnalysisDataRow): string {
    if (!row) return '';
    return (this.config.tooltip || [])
      .map((field) => `<div><strong>${this.toLabel(field)}</strong>: ${this.formatTooltipValue(field, row[field])}</div>`)
      .join('');
  }

  private formatTooltipValue(field: string, value: unknown): string {
    if (field.toLowerCase().includes('price')) return this.formatCurrency(Number(value || 0));
    if (field === 'customerDiscountPercent') return `${Number(value || 0).toFixed(1)}%`;
    return String(value ?? '-');
  }

  private formatCurrency(value: number): string {
    if (!Number.isFinite(value)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
    }).format(value);
  }

  private formatCompactCurrency(value: number): string {
    if (!Number.isFinite(value)) return '-';
    const absoluteValue = Math.abs(value);
    const divisor = absoluteValue >= 1000000 ? 1000000 : absoluteValue >= 1000 ? 1000 : 1;
    const suffix = divisor === 1000000 ? 'M' : divisor === 1000 ? 'K' : '';
    return `$${Math.round(value / divisor)}${suffix}`;
  }

  private formatCompactNumber(value: number): string {
    if (!Number.isFinite(value)) return '-';
    const absoluteValue = Math.abs(value);
    const divisor = absoluteValue >= 1000000 ? 1000000 : absoluteValue >= 1000 ? 1000 : 1;
    const suffix = divisor === 1000000 ? 'M' : divisor === 1000 ? 'K' : '';
    return `${Math.round(value / divisor)}${suffix}`;
  }

  private formatSecondaryXAxisLabel(row: PriceAnalysisDataRow, config: any): string {
    const valueField = String(config?.valueField || config?.field || 'country');
    const bracketField = String(config?.bracketField || config?.parentField || 'region');
    const rawValue = String(row?.[valueField] ?? '').trim();
    const value = this.formatSecondaryAxisValue(rawValue, config);
    const bracketValue = String(row?.[bracketField] ?? '').trim();
    const separator = String(config?.separator ?? ' ');

    if (config?.format) {
      return String(config.format)
        .replace(/\{value\}/g, value)
        .replace(/\{bracket\}/g, bracketValue)
        .replace(/\{country\}/g, String(row?.country ?? '').trim())
        .replace(/\{region\}/g, String(row?.region ?? '').trim());
    }

    if (value && bracketValue) return `${value}${separator}(${bracketValue})`;
    return value || bracketValue || '-';
  }

  private formatSecondaryAxisValue(value: string, config: any): string {
    const format = String(config?.valueFormat || '').toLowerCase();
    if (format !== 'country-code' && format !== 'countrycode' && format !== 'iso3') {
      return value;
    }

    const countryCodes: Record<string, string> = {
      australia: 'AUS',
      canada: 'CAN',
      germany: 'DEU',
      india: 'IND',
      us: 'USA',
      usa: 'USA',
      'united states': 'USA',
      'united states of america': 'USA',
      brazil: 'BRA',
      chile: 'CHL',
      peru: 'PER',
      mexico: 'MEX',
      china: 'CHN',
      japan: 'JPN',
      'south africa': 'ZAF',
      indonesia: 'IDN',
    };

    const normalized = value.trim().toLowerCase();
    return countryCodes[normalized] || value.slice(0, 3).toUpperCase();
  }

  private resolveSecondaryXAxisLabels(rows: PriceAnalysisDataRow[], config: any): string[] {
    const labels = rows.map((row) => this.formatSecondaryXAxisLabel(row, config));
    if (config?.combineSameLabels === false) {
      return labels;
    }

    const groupedLabels = labels.map(() => '');
    let start = 0;

    while (start < labels.length) {
      const label = labels[start];
      let end = start;
      while (end + 1 < labels.length && labels[end + 1] === label) {
        end += 1;
      }

      const middle = Math.floor((start + end) / 2);
      groupedLabels[middle] = label;
      start = end + 1;
    }

    return groupedLabels;
  }

  private toLabel(value: string): string {
    return value.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase());
  }

  private updateComparisonGraphics(): void {
    if (!this.myChart) return;
    const graphic = this.config.layout?.showDiscountCallouts ? this.buildComparisonGraphics() : [];
    this.myChart.setOption({ graphic }, false);
  }

  private buildComparisonGraphics(): any[] {
    if (!this.myChart || !this.visibleRows.length || (this.config.series || []).length < 2) {
      return [];
    }

    const comparison = (this.config as any).comparison || {};
    const baseIndex = Number.isInteger(comparison.baseSeries) ? Number(comparison.baseSeries) : 0;
    const compareIndex = Number.isInteger(comparison.compareSeries) ? Number(comparison.compareSeries) : 1;
    const baseSeries = this.config.series?.[baseIndex];
    const compareSeries = this.config.series?.[compareIndex];
    if (!baseSeries || !compareSeries || baseIndex === compareIndex) {
      return [];
    }

    const seriesCount = this.config.series?.length || 0;
    const rawBarWidth = Number((this.config as any).barWidth ?? (baseSeries as any).barWidth ?? 16);
    const barWidth = Number.isFinite(rawBarWidth) ? rawBarWidth : 16;
    const gap = Number(comparison.seriesGap ?? (this.config as any).seriesGap ?? 6);
    const groupWidth = (seriesCount * barWidth) + (Math.max(0, seriesCount - 1) * gap);
    const lineColor = comparison.lineColor || '#111111';
    const bubbleFill = comparison.bubbleBackground || '#ffffff';
    const bubbleText = comparison.textColor || '#111111';
    const bubbleBorder = comparison.bubbleBorderColor || '#111111';
    const bubbleHeight = Number(comparison.bubbleHeight ?? 22);
    const lineLift = Number(comparison.lineLift ?? 26);
    const stemGap = Number(comparison.stemGap ?? 8);
    const arrowSize = Number(comparison.arrowSize ?? 5);
    const valueLabelClearance = Number(comparison.valueLabelClearance ?? 26);
    const decimals = Number(comparison.valueDecimals ?? 1);
    const suffix = String(comparison.valueSuffix ?? '%');
    const minBubbleTop = Number(comparison.minBubbleTop ?? 4);
    const chartWidth = this.myChart?.getWidth?.() ?? 0;
    const graphics: any[] = [];

    this.visibleRows.forEach((row, index) => {
      const baseValue = Number(row[baseSeries.key]);
      const compareValue = Number(row[compareSeries.key]);
      if (!Number.isFinite(baseValue) || !Number.isFinite(compareValue) || baseValue === 0) {
        return;
      }

      const percent = comparison.valueKey && row[comparison.valueKey] !== undefined
        ? Number(row[comparison.valueKey])
        : ((compareValue - baseValue) / baseValue) * 100;
      if (!Number.isFinite(percent)) {
        return;
      }

      const centerX = Number(this.myChart?.convertToPixel({ xAxisIndex: 0 }, index));
      const basePoint = this.myChart?.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [index, baseValue]) as number[];
      const comparePoint = this.myChart?.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [index, compareValue]) as number[];
      if (!Number.isFinite(centerX) || !Array.isArray(basePoint) || !Array.isArray(comparePoint)) {
        return;
      }

      const baseX = centerX - (groupWidth / 2) + (baseIndex * (barWidth + gap)) + (barWidth / 2);
      const compareX = centerX - (groupWidth / 2) + (compareIndex * (barWidth + gap)) + (barWidth / 2);
      const baseArrowY = basePoint[1] - valueLabelClearance;
      const compareArrowY = comparePoint[1] - valueLabelClearance;
      const topY = Math.min(baseArrowY, compareArrowY);
      const minLineY = minBubbleTop + bubbleHeight + 8;
      const maxLineY = topY - arrowSize - stemGap - 4;
      const lineY = Math.min(Math.max(topY - lineLift, minLineY), maxLineY);
      if (!Number.isFinite(lineY) || lineY >= topY - arrowSize) {
        return;
      }

      const leftX = Math.min(baseX, compareX);
      const rightX = Math.max(baseX, compareX);
      const label = `${percent > 0 ? '+' : ''}${percent.toFixed(decimals)}${suffix}`;
      const bubbleWidth = Math.max(44, Math.round((label.length * 7.2) + 18));
      const centerBubbleX = (leftX + rightX) / 2;
      const bubbleX = chartWidth > 0
        ? Math.max((bubbleWidth / 2) + 4, Math.min(centerBubbleX, chartWidth - (bubbleWidth / 2) - 4))
        : centerBubbleX;
      const lineBottomY = lineY + 2;

      graphics.push({
        type: 'group',
        silent: true,
        z: 20,
        children: [
          {
            type: 'line',
            shape: { x1: leftX, y1: lineY, x2: rightX, y2: lineY },
            style: { stroke: lineColor, lineWidth: 1.25 },
          },
          {
            type: 'line',
            shape: { x1: baseX, y1: lineBottomY, x2: baseX, y2: baseArrowY - stemGap },
            style: { stroke: lineColor, lineWidth: 1.25 },
          },
          {
            type: 'line',
            shape: { x1: compareX, y1: lineBottomY, x2: compareX, y2: compareArrowY - stemGap },
            style: { stroke: lineColor, lineWidth: 1.25 },
          },
          {
            type: 'polygon',
            shape: {
              points: [
                [baseX, baseArrowY],
                [baseX - arrowSize, baseArrowY - arrowSize - 2],
                [baseX + arrowSize, baseArrowY - arrowSize - 2],
              ],
            },
            style: { fill: lineColor },
          },
          {
            type: 'polygon',
            shape: {
              points: [
                [compareX, compareArrowY],
                [compareX - arrowSize, compareArrowY - arrowSize - 2],
                [compareX + arrowSize, compareArrowY - arrowSize - 2],
              ],
            },
            style: { fill: lineColor },
          },
          {
            type: 'rect',
            shape: {
              x: bubbleX - (bubbleWidth / 2),
              y: lineY - bubbleHeight - 6,
              width: bubbleWidth,
              height: bubbleHeight,
              r: bubbleHeight / 2,
            },
            style: {
              fill: bubbleFill,
              stroke: bubbleBorder,
              lineWidth: 1,
            },
          },
          {
            type: 'text',
            style: {
              text: label,
              x: bubbleX,
              y: lineY - 6 - (bubbleHeight / 2),
              fill: bubbleText,
              fontSize: 11,
              fontWeight: 700,
              align: 'center',
              verticalAlign: 'middle',
            },
          },
        ],
      });
    });

    return graphics;
  }
}
